import { DTMF_KEYS , ACTION_COLORS} from '../../../../../utilities/constant'
import { toast } from 'react-toastify';


const getDtmfIndex = (edge) => {
    const key = edge.sourceHandle?.split('-').pop();
    const idx = DTMF_KEYS.indexOf(key);
    return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
};

// Recursive function to calculate left-most path duration
const calculateLeftPathDuration = (node, nodesArray, edges, visited = new Set()) => {
    if (!node) return 0;

    // Always add the node's own duration first
    let duration = 0;
    if (node.data?.verb === "play") {
        duration += node.data?.action?.duration || 0;
    } else if (node.data?.verb === "record") {
        duration += node.data?.action?.duration || 0;
    } else if (node.data?.verb === "dtmf" || node.data?.verb === "gather") {
        duration += Number(node.data?.timeout ?? 0);
        duration += node.data?.action?.menu?.duration ?? 0;
    }

    // Avoid infinite loops by marking visited AFTER counting current node
    if (visited.has(node.id)) return duration;
    visited.add(node.id);

    const edgeData = edges.filter(e => e.source === node.id);

    if (node.data?.verb === "dtmf") {
        const sortedEdgeData = [...edgeData].sort((a, b) => getDtmfIndex(a) - getDtmfIndex(b));
        if (sortedEdgeData.length > 0) {
            const firstEdge = sortedEdgeData[0];
            const targetNode = nodesArray.find(n => n.id === firstEdge.target);
            duration += calculateLeftPathDuration(targetNode, nodesArray, edges, visited);
        }
    } else if (edgeData.length > 0) {
        const firstEdge = edgeData[0];
        const targetNode = nodesArray.find(n => n.id === firstEdge.target);
        duration += calculateLeftPathDuration(targetNode, nodesArray, edges, visited);
    }

    return duration;
};

export const transformNodes = ({
    nodesArray,
    edges,
    flowName,
    scheduleData,
    isGatherNode,
    userId,
    smsWebhookFlag
}) => {
    const cid = userId;
    const nodeIdMap = new Map(nodesArray.map((node, index) => [node.id, index]));

    // Start from the first node in nodesArray (assume it's root)
    const totalDuration = calculateLeftPathDuration(nodesArray[0], nodesArray, edges);
    const dtmfTargetNodeIds = new Set();

    const transformedNodes = nodesArray.reduce((acc, node, index) => {
        const edgeData = edges.filter(obj => obj.source === node.id);
        const nodeId = nodeIdMap.get(node.id);

        const flowJson = { verb: node.data?.verb };

        if (node.data?.verb === "play") {
            flowJson.action = node.data?.action;
            flowJson.duration = node.data?.action?.duration || 0;
        }
        if (node.data?.verb === "record") {
            flowJson.action = node.data?.action;
            flowJson.duration = node.data?.action?.duration || 0;
            flowJson.record_duration = node.data?.record_duration
        }
        else if (node.data?.verb === "dtmf" || node.data?.verb === "gather") {
            flowJson.type = node.data?.type;
            flowJson.action = node.data?.action;
            flowJson.numDigits = node.data?.numDigits;
            flowJson.timeout = node.data?.timeout;
            flowJson.numSources = node.data?.numSources;
            flowJson.rePrompt = node.data?.rePrompt;
            flowJson.ttsRows = node.data?.ttsRows ?? [];
            flowJson.minValue = node.data?.minValue ?? "";
            flowJson.smsData = node.data?.smsData ?? {};
            flowJson.manualDigits = node.data?.manualDigits ?? ""
        }
        else if (node.data?.verb === "transfer") {
            flowJson.action = node.data?.action;
        }

        let flowControl = {};

        if (node.data?.verb === "dtmf"  && Object.keys(node.data?.selectedHandles ?? {}).length !== 0) {
            const sortedEdgeData = [...edgeData].sort((a, b) => getDtmfIndex(a) - getDtmfIndex(b));
            sortedEdgeData.forEach(edge => {
                const key = edge.sourceHandle?.split('-').pop();
                const targetNumericId = nodeIdMap.get(edge.target);
                const targetNode = nodesArray.find(n => n.id === edge.target);

                if (targetNode.data?.verb === "play" || targetNode.data?.verb === "record") dtmfTargetNodeIds.add(targetNode.id);
                if (!key || targetNumericId === undefined) return;

                flowControl[`dtmf_${key}`] = {
                    flowName,
                    nextNodeId: targetNumericId,
                    isLoop: edge.source === edge.target,
                    parent: node.data?.parent ?? ""
                };
            });
        }
        else if (edgeData.length > 0) {
            const targetNumericId = nodeIdMap.get(edgeData[0].target);
            if (targetNumericId) {
                flowControl.default = {
                    flowName,
                    nextNodeId: targetNumericId,
                    parent: node.data?.parent ?? ""
                };
            }
        }

        acc[nodeId] = {
            id: nodeId,
            flowJson,
            flowRender: { ...node },
            flowControl
        };

        return acc;
    }, {});

    return {
        flowName,
        userId: cid,
        nodes: transformedNodes,
        scheduleData,
        isGatherNode,
        promptDuration: totalDuration,
        smsWebhookFlag
    };
};

export const validateIVR = ({ nodes = [], edges = [], flowName, scheduleData}) => {
    console.log('scheduleData', scheduleData)
  if (!nodes.length) {
      toast.warning("Add at least one node.")
    return false;
  }

  if (!flowName) {
      toast.warning("IVR Name required.")
    return false;
  }

  if (Object.keys(scheduleData).length <= 0) {
    toast.warning("Time Schedule required.")
    return false;
  }

  // 🔹 Build edge maps for O(1) lookups
  const incomingMap = new Map();
  const outgoingMap = new Map();

  for (const edge of edges) {
    if (!incomingMap.has(edge.target)) {
      incomingMap.set(edge.target, []);
    }
    if (!outgoingMap.has(edge.source)) {
      outgoingMap.set(edge.source, []);
    }

    incomingMap.get(edge.target).push(edge);
    outgoingMap.get(edge.source).push(edge);
  }

  for (const node of nodes) {
    const incoming = incomingMap.get(node.id) || [];
    const outgoing = outgoingMap.get(node.id) || [];
    const label = String(node?.data?.label ?? "").toUpperCase();


    /* Rule 1: End nodes must be Hangup nodes */
    if (incoming.length === 0 && outgoing.length === 0 && node.type !== "hangupNode") {
          toast.warning(`Source and Handle of "${label}" must be a Connected to a node`)
      return false;
    }

    /* Rule 2: Input handle must have at least one connection (except answer nodes) */
    if (
      node.type !== "answerNode" &&
      incoming.length === 0
    ) {
      toast.warning(`Node "${label}" must have at least one input connected`)
      return false;
    }

    /* Rule 3: Selected output handles must be connected */
    const selectedHandles = node.data?.selectedHandles;
    if (selectedHandles) {
      for (const [handleId, handleObj] of Object.entries(selectedHandles)) {
        if (!handleObj || Object.keys(handleObj).length === 0) continue;

        const isConnected = outgoing.some(
          edge => edge.sourceHandle === handleId
        );

        if (!isConnected) {
          const dtmf = handleId.split("-")[1];
          toast.warning(`Output handle "DTMF_${dtmf}" of node "${label}" must be connected`)
          return false;
        }
      }
    }



    /* Rule 4: End nodes must be Hangup nodes */
    if (outgoing.length === 0 && node.type !== "hangupNode") {
          toast.warning(`End node "${label}" must be a Hangup node`)
      return false;
    }



    /* Rule 5: Play node must have audio */
    if (node.type === "playNode" && !node?.data?.action?.audio) {
          toast.warning(`Node "${label}" must select an audio file`)
      return false;
    }

        /* Rule 6: Record node must have audio ad record_duration*/
        if (node.type === "recordNode") {
            const action = node?.data?.action || {};
            const duration = action?.duration;

            if (!action?.audio) {
                toast.error(`Node "${label}" must select an audio file`);
                return false;
            }

            if (!duration || !Number.isInteger(Number(duration)) || Number(duration) <= 0) {
                toast.error(`Node "${label}" must have a valid record duration (positive integer)`);
                return false;
            }
        }

    /* Rule 7: Gather node must have menu audio */
    if (
      node.type === "gatherNode" &&
      !node?.data?.action?.menu?.audio
    ) {
          toast.warning(`Node "${label}" must select an audio file`)
      return false;
    }
  }

  return true;
};